import React, { useEffect, useRef, useState } from 'react';
import { Menu, X, Download } from 'lucide-react';
import { NAV, HEADER_SOCIALS, NavId, SocialId } from '@/config/site';
import { SocialLink } from './Social';
import { scrollToSection } from '@/lib/scroll';
import { installBus, type InstallState } from '@/lib/installBus';

const INTRO_KEY = 'ek-logo-intro-seen';

/** First visit of the session: the logo fades up out of a soft sunset glow (skipped for reduced motion). */
function useLogoIntro() {
  const [phase, setPhase] = useState<'off' | 'wait' | 'play'>(() => {
    try {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return 'off';
      if (sessionStorage.getItem(INTRO_KEY)) return 'off';
      return 'wait';
    } catch {
      return 'off';
    }
  });
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (phase !== 'wait') return;
    const start = () => {
      setPhase('play');
      try {
        sessionStorage.setItem(INTRO_KEY, '1');
      } catch {
        /* private mode */
      }
    };
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth) {
      const r = requestAnimationFrame(start);
      return () => cancelAnimationFrame(r);
    }
    img?.addEventListener('load', start, { once: true });
    const t = window.setTimeout(start, 1500); // never keep the logo hidden
    return () => {
      img?.removeEventListener('load', start);
      window.clearTimeout(t);
    };
  }, [phase]);
  useEffect(() => {
    if (phase !== 'play') return;
    const t = window.setTimeout(() => setPhase('off'), 2600);
    return () => window.clearTimeout(t);
  }, [phase]);
  return { phase, imgRef };
}

export default function Header({ active, socials }: { active: NavId; socials: Record<string, string> }) {
  const { phase, imgRef } = useLogoIntro();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [install, setInstall] = useState<InstallState>({ status: 'idle' });
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => installBus.on(setInstall), []);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);
  const visibleSocials = HEADER_SOCIALS.filter((id) => (socials[id] || '').trim());
  const closeAndGo = (target: string) => { setMenuOpen(false); scrollToSection(target); };
  return (
    <header className={`ek-header ${scrolled ? 'is-scrolled' : ''}`} data-testid="site-header">
      <a
        href="#home"
        className={`ek-logo ${phase === 'wait' ? 'is-intro-wait' : ''} ${phase === 'play' ? 'is-intro' : ''}`}
        aria-label="The Ekuba Project — home"
        data-testid="header-logo"
        data-intro={phase}
        onClick={(e) => {
          e.preventDefault();
          scrollToSection('home');
        }}
      >
        {phase !== 'off' && <span className="ek-logo-glow" aria-hidden="true" data-testid="header-logo-glow" />}
        <picture>
          <source srcSet="/assets/brand/ekuba-logo.webp" type="image/webp" />
          <img
            ref={imgRef}
            className="ek-logo-img"
            src="/assets/brand/ekuba-logo.png"
            alt="The Ekuba Project"
            width={900}
            height={447}
            draggable={false}
            fetchPriority="high"
            data-testid="header-logo-image"
          />
        </picture>
      </a>

      <nav className="ek-nav" aria-label="Main">
        {NAV.map((n) => (
          <a
            key={n.id}
            href={`#${n.target}`}
            className={`ek-nav-link ${active === n.id ? 'is-active' : ''}`}
            style={{ ['--x' as string]: `${n.x}px` } as React.CSSProperties}
            data-testid={`nav-${n.id}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(n.target);
            }}
          >
            {n.label}
          </a>
        ))}
      </nav>

      <div className="ek-header-social">
        {visibleSocials.map((id: SocialId) => (
          <SocialLink
            key={id}
            id={id}
            url={socials[id] || ''}
            size={id === 'facebook' ? 15 : id === 'soundcloud' ? 16 : 17}
            className={`ek-hs ek-hs-${id}`}
            testPrefix="header-social"
          />
        ))}
      </div>

      <button
        type="button"
        className={`ek-burger ${menuOpen ? 'is-open' : ''}`}
        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={menuOpen}
        aria-controls="ek-mobile-drawer"
        onClick={() => setMenuOpen((o) => !o)}
        data-testid="mobile-menu-toggle"
      >
        {menuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {menuOpen && (
        <div className="ek-drawer-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}
      <aside
        id="ek-mobile-drawer"
        className={`ek-drawer ${menuOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-label="Mobile navigation"
        aria-modal="true"
        data-testid="mobile-drawer"
      >
        <nav className="ek-drawer-nav" aria-label="Main (mobile)">
          {NAV.map((n) => (
            <button
              key={n.id}
              type="button"
              className={`ek-drawer-link ${active === n.id ? 'is-active' : ''}`}
              onClick={() => closeAndGo(n.target)}
              data-testid={`mobile-nav-${n.id}`}
            >
              {n.label}
            </button>
          ))}
        </nav>
        {visibleSocials.length > 0 && (
          <div className="ek-drawer-socials">
            {visibleSocials.map((id: SocialId) => (
              <SocialLink
                key={id}
                id={id}
                url={socials[id] || ''}
                size={18}
                className={`ek-ds ek-ds-${id}`}
                testPrefix="mobile-social"
              />
            ))}
          </div>
        )}
        {(install.status === 'available' || install.status === 'ios') && (
          <button
            type="button"
            className="ek-drawer-install"
            onClick={async () => {
              setMenuOpen(false);
              if (install.status === 'available') {
                await install.promptNow();
              } else if (install.status === 'ios') {
                // trigger the InstallPrompt's iOS guide by clearing snooze + reloading show
                try { localStorage.removeItem('ek-install-dismissed-v1'); } catch { /* noop */ }
                window.dispatchEvent(new Event('ek-show-ios-install'));
              }
            }}
            data-testid="drawer-install-app"
          >
            <Download size={16} /> Install the app
            <span className="ek-drawer-install-sub">
              {install.status === 'ios' ? 'Add to Home Screen on iPhone / iPad' : 'Full-screen player on your device'}
            </span>
          </button>
        )}
        <div className="ek-drawer-foot">
          <a href="/press" className="ek-drawer-foot-link">Press Kit</a>
          <a href="/legal/privacy" className="ek-drawer-foot-link">Privacy</a>
          <a href="/legal/terms" className="ek-drawer-foot-link">Terms</a>
        </div>
      </aside>
    </header>
  );
}
